import { useState, useEffect, useCallback } from 'react';

const reviews = [
  {
    name: 'Marisol Gh',
    avatar: 'M',
    rating: 5,
    text: 'Excelente servicio y atención. El equipo es muy profesional y me hicieron sentir cómoda desde la primera consulta. Mi sonrisa ha cambiado completamente.',
    service: 'Blanqueamiento dental',
    avatarBg: 'from-[#3A86FF] to-[#0A2540]',
  },
  {
    name: 'Lore Naba',
    avatar: 'L',
    rating: 5,
    text: 'Muy profesional el trato y los resultados superaron mis expectativas. La clínica es muy moderna y limpia. Definitivamente regresaré para más tratamientos.',
    service: 'Rehabilitación dental',
    avatarBg: 'from-emerald-400 to-emerald-700',
  },
  {
    name: 'Juan Fernando',
    avatar: 'JF',
    rating: 5,
    text: 'Recomiendo ampliamente la clínica. Llevaba años postergando mi tratamiento por miedo, pero aquí me trataron con mucha paciencia y cuidado. ¡Excelente lugar!',
    service: 'Ortodoncia',
    avatarBg: 'from-violet-400 to-violet-700',
  },
  {
    name: 'Ana Laura P.',
    avatar: 'AL',
    rating: 5,
    text: 'Me realizaron una limpieza profunda y coronas. El trabajo quedó perfecto y el dolor fue mínimo. La Dra. es increíble, muy amable y explicó todo el proceso.',
    service: 'Prótesis y coronas',
    avatarBg: 'from-amber-400 to-amber-600',
  },
  {
    name: 'Carlos Mendoza',
    avatar: 'CM',
    rating: 5,
    text: 'Las instalaciones son modernas y muy limpias. El personal es atento y profesional. El resultado de mi tratamiento fue excelente. 100% recomendado.',
    service: 'Estética dental',
    avatarBg: 'from-rose-400 to-rose-700',
  },
];

const VISIBLE = 3; // cards visible at once on desktop

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const total = reviews.length;

  const go = useCallback(
    (next: number) => {
      if (animating) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent((next + total) % total);
        setAnimating(false);
      }, 300);
    },
    [animating, total]
  );

  const prev = () => go(current - 1);
  const next = () => go(current + 1);

  // Auto-advance every 5 s
  useEffect(() => {
    const id = setInterval(() => go(current + 1), 5000);
    return () => clearInterval(id);
  }, [current, go]);

  // Build the 3 indices to show
  const indices = Array.from({ length: VISIBLE }, (_, i) => (current + i) % total);

  return (
    <section id="testimonials" className="py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-16 scroll-animate">
          <span className="inline-block text-[#3A86FF] font-semibold text-sm tracking-widest uppercase mb-3">
            Lo que dicen nuestros pacientes
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A2540] mb-5">
            Opiniones{' '}
            <span className="bg-gradient-to-r from-[#3A86FF] to-[#0A2540] bg-clip-text text-transparent">
              Verificadas
            </span>
          </h2>

          {/* Rating summary */}
          <div className="inline-flex items-center gap-3 bg-[#f7f9fc] rounded-2xl px-6 py-3 border border-gray-100">
            <div className="flex gap-0.5 items-center">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-400 text-xl" />
              ))}
            </div>
            <span className="text-[#0A2540] font-extrabold text-2xl">5.0</span>
            <span className="text-gray-400 text-sm">basado en 15 opiniones</span>
          </div>
        </div>

        {/* Carousel track */}
        <div className="relative">
          {/* Cards */}
          <div
            className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-opacity duration-300 ${
              animating ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {indices.map((revIdx, pos) => {
              const rev = reviews[revIdx];
              const isCenter = pos === 1;
              return (
                <div
                  key={`${revIdx}-${pos}`}
                  className={`relative rounded-3xl p-7 border transition-all duration-300 flex flex-col ${
                    isCenter
                      ? 'bg-[#0A2540] border-[#3A86FF]/30 scale-105 shadow-[0_20px_60px_rgba(10,37,64,0.25)]'
                      : 'bg-white border-gray-100 shadow-sm'
                  }`}
                >
                  {/* Quote icon */}
                  <div
                    className={`absolute top-6 right-6 opacity-20 ${
                      isCenter ? 'text-white' : 'text-[#3A86FF]'
                    }`}
                  >
                    <i className="ri-double-quotes-r text-4xl" />
                  </div>

                  {/* Stars */}
                  <div className="flex gap-0.5 mb-4 items-center">
                    {[...Array(rev.rating)].map((_, s) => (
                      <i key={s} className="ri-star-fill text-yellow-400 text-sm" />
                    ))}
                  </div>

                  {/* Text */}
                  <p
                    className={`text-sm leading-relaxed mb-6 flex-1 ${
                      isCenter ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    "{rev.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br ${rev.avatarBg} text-white font-bold text-sm flex-shrink-0`}
                    >
                      {rev.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-semibold text-sm truncate ${
                          isCenter ? 'text-white' : 'text-[#0A2540]'
                        }`}
                      >
                        {rev.name}
                      </div>
                      <div
                        className={`text-xs truncate ${
                          isCenter ? 'text-[#3A86FF]' : 'text-gray-400'
                        }`}
                      >
                        {rev.service}
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-400 text-sm" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Arrow — prev */}
          <button
            onClick={prev}
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white border border-gray-200 hover:border-[#3A86FF] hover:bg-[#3A86FF] text-[#0A2540] hover:text-white rounded-full transition-all duration-200 cursor-pointer shadow-sm z-10"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>

          {/* Arrow — next */}
          <button
            onClick={next}
            className="absolute -right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white border border-gray-200 hover:border-[#3A86FF] hover:bg-[#3A86FF] text-[#0A2540] hover:text-white rounded-full transition-all duration-200 cursor-pointer shadow-sm z-10"
            aria-label="Siguiente"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-10">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => go(i)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                i === current ? 'w-6 h-2 bg-[#3A86FF]' : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'
              }`}
              aria-label={`Ir a opinión ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
